import React, { useEffect, useState } from "react";
import Select from "react-select";
import { medications } from "../data/medicationData";
import { nonInvasiveDevices } from "../data/nonInvasiveDevicesData";
import { nutrition } from "../data/nutritiondata";
import { procedures } from "../data/procedureData";
import { sleep } from "../data/sleepData";
import { therapies } from "../data/therapyData";
import { imaging } from "../data/imagingData";
import { alternativetreatment } from "../data/alternativeData";
import { braintraining } from "../data/brainData";
import { useNavigate } from "react-router-dom";
import FeedbackForm from "../components/FeedbackForm";
import LoginModal from "../components/LoginModal"; // <-- restored login modal
import ProfileMenu from "../components/ProfileMenu";
import "../App.css";

const categoryMap = {
  medications,
  devices: nonInvasiveDevices,
  nutrition,
  procedures,
  sleep,
  therapies,
  imaging,
  alternative: alternativetreatment,
  brain: braintraining,
};

const categoryLabels = {
  medications: "Medications",
  devices: "Medical Devices",
  nutrition: "Supplements & Nutrition",
  procedures: "Surgical Procedures",
  sleep: "Sleep",
  therapies: "Therapies",
  imaging: "Imaging",
  alternative: "Alternative Treatments",
  brain: "Biofeedback Techniques",
};

const PersonalizedPage = () => {
  const [groupedData, setGroupedData] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [expandedStudies, setExpandedStudies] = useState({});
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [user, setUser] = useState({});
  const [conditionList, setConditionList] = useState([]);
  const [selectedConditions, setSelectedConditions] = useState(new Set());
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLogin, setShowLogin] = useState(false); 
  const navigate = useNavigate();

  const doLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");           
    window.location.reload();
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const userConditions = storedUser.conditions?.map(c => c.toLowerCase()) || [];
    setUser(storedUser);
    setConditionList(userConditions);

    const grouped = {};
    Object.entries(categoryMap).forEach(([category, dataset]) => {
      Object.entries(dataset).forEach(([key, entry]) => {
        const matchedConditions = entry.conditions?.filter(cond =>
          userConditions.includes(cond.toLowerCase())
        );
        if (matchedConditions?.length > 0) {
          matchedConditions.forEach(cond => {
            if (!grouped[cond]) grouped[cond] = {};
            grouped[cond][entry.name] = {
              resources: entry.resources || [],
              description: entry.description,
              category,
            };
          });
        }
      });
    });
    setGroupedData(grouped);
    setSelectedConditions(new Set());
    setSelectedCategories(new Set());
  }, []);

  const formatConditions = (conditions) => {
    if (conditions.length === 1) return conditions[0];
    if (conditions.length === 2) return `${conditions[0]} and ${conditions[1]}`;
    return `${conditions.slice(0, -1).join(", ")}, and ${conditions[conditions.length - 1]}`;
  };

  const toggleDescription = (condition, itemName) => {
    const key = `${condition}__${itemName}`;
    setExpandedDescriptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleStudies = (condition, itemName) => {
    const key = `${condition}__${itemName}`;
    setExpandedStudies(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleViewSummary = (summary) => {
    setSelectedSummary(summary);
  };

  const handleCloseSummary = () => {
    setSelectedSummary(null);
  };

  const renderConditionDropdown = () => {
    const allOption = { value: "all", label: "All Conditions" };
    const options = [allOption, ...conditionList.map(cond => ({ value: cond, label: cond }))];
    const value = [...selectedConditions].map(cond => ({ value: cond, label: cond }));
    return (
      <div className="dropdown-wrapper">
        <Select
          isMulti
          placeholder="Select conditions..."
          value={value}
          onChange={(opts) => {
            const selected = opts.map(opt => opt.value);
            const isAll = selected.includes("all");
            setSelectedConditions(isAll ? new Set(conditionList) : new Set(selected));
          }}
          options={options}
          className="category-select"
          classNamePrefix="react-select"
        />
      </div>
    );
  };

  const renderCategoryDropdown = () => {
    const allOption = { value: "all", label: "All Categories" };
    const options = [allOption, ...Object.keys(categoryMap).map(cat => ({
      value: cat,
      label: categoryLabels[cat],
    }))];
    const value = [...selectedCategories].map(cat => ({
      value: cat,
      label: categoryLabels[cat],
    }));
    return (
      <Select
        isMulti
        placeholder="Select treatment types..."
        value={value}
        onChange={(opts) => {
          const selected = opts.map(opt => opt.value);
          const isAll = selected.includes("all");
          setSelectedCategories(isAll ? new Set(Object.keys(categoryMap)) : new Set(selected));
        }}
        options={options}
        className="category-select"
        classNamePrefix="react-select"
      />
    );
  };

  const hasMultipleConditions = conditionList.length > 1;
  const activeConditions = selectedConditions.size > 0 ? [...selectedConditions] : [];
  const activeCategories = selectedCategories.size > 0 ? [...selectedCategories] : [];

  const rawEntries = hasMultipleConditions
    ? (activeConditions.length > 0 && activeCategories.length > 0
      ? Object.entries(groupedData)
        .filter(([condition]) => activeConditions.includes(condition))
        .flatMap(([condition, entries]) =>
          Object.entries(entries)
            .filter(([_, entry]) => activeCategories.includes(entry.category))
            .map(([itemName, entry]) => ({ name: itemName, condition, ...entry }))
        )
      : [])
    : (activeCategories.length > 0
      ? Object.entries(groupedData)
        .flatMap(([condition, entries]) =>
          Object.entries(entries)
            .filter(([_, entry]) => activeCategories.includes(entry.category))
            .map(([itemName, entry]) => ({ name: itemName, condition, ...entry }))
        )
      : []);

  const seen = new Set();
  const combinedEntries = rawEntries.filter(entry => {
    const key = `${entry.name}-${entry.category}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return (
    <>
      <div style={{ background: "#d1e8fd", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* Breadcrumb is above the white nav bar */}
        <div
          style={{
            maxWidth: "1090px",
            width: "90%",
            margin: "24px auto -8px",
            padding: "0 20px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            boxSizing: "border-box"
          }}
        >
         {user ? (
          <ProfileMenu
            user={user}
            onProfile={() => navigate("/profile")}
            onLogout={doLogout}
            />
            ) : (
            <button className="nav-button" onClick={() => setShowLogin(true)}>
            Login
            </button>
            )}
        </div>

  
        {/* White nav bar with buttons */}
        <div
          className="top-nav"
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#ffffff",
            padding: "12px 20px",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            marginBottom: "20px",
            width: "90%",
            maxWidth: "1020px",
          }}
        >
          <button className="nav-button nav-button-explore" onClick={() => navigate("/")}>
            Explore Database
          </button>

          <div className="mobile-button-row">
            <button className="nav-button" onClick={() => setShowFeedback(true)}>
              Give Feedback
            </button>
            {/* LOGIN MODAL */}
            {showLogin && (
                  <LoginModal
                    onClose={() => setShowLogin(false)}
                    onLogin={(userData) => {
                      setUser(userData);
                      setShowLogin(false); // close modal, stay on page
                    }}
                  />
                )}
          </div>
        </div>

          </div>

  

      <div className="home-wrapper">
        <div className="home-container">
          <h1 style={{ marginBottom: "8px" }}>Personalized Research & Treatments</h1>
          <p className="sidebar-description">
            {user?.username && `Welcome, ${user.username}. `}
            This is your personalized guide for {formatConditions(conditionList)}.
            Here you’ll find real studies, trusted treatments, and clear details on what to expect — including side effects and long-term consequences.
            We update it often, so you always have the latest research to help you decide what might work best for you and what to try next.
            You’re not alone in this — and we hope this helps you move forward with more clarity, options, and hope.
          </p>

          <div
            className="filters-row"
            style={{
              display: "flex",
              flexDirection: conditionList.length > 1 ? "row" : "column",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            {hasMultipleConditions && (
              <div className="dropdown-col">
                {renderConditionDropdown()}
              </div>
            )}
            <div className="dropdown-col">
              {renderCategoryDropdown()}
            </div>

          </div>


          {combinedEntries.length > 0 && (
            <div style={{ marginTop: "32px" }}>
              <h2 style={{ fontSize: "1.4rem", marginBottom: "0", color: "#2c3e50", textAlign: "left" }}>
                Treatments for {formatConditions(hasMultipleConditions ? activeConditions : conditionList)}:
              </h2>

              {combinedEntries.map(({ name, resources, description, category, condition }) => {
                const descKey = `${condition}__${name}`;
                const studyKey = `${condition}__${name}`;
                const showDesc = expandedDescriptions[descKey];
                const showAllStudies = expandedStudies[studyKey];

                return (
                  <div key={descKey} style={{ marginBottom: "32px" }}>
                    <div className="medication-bar" style={{ position: "relative", marginTop: "16px" }}>
                      <div className="medication-name">{name}</div>
                      {description && (
                        <button
                          className="expand-button"
                          onClick={() => toggleDescription(condition, name)}
                          title={showDesc ? "Collapse" : "Expand"}
                        >
                          {showDesc ? "−" : "..."}
                        </button>
                      )}
                    </div>

                    {description && showDesc && (
                      <div className="description-box">{description}</div>
                    )}

                    <div className="research-grid" style={{ marginTop: "16px" }}>
                      {resources.slice(0, showAllStudies ? resources.length : 3).map((paper, i) => (
                        <div key={i} className="research-card" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                          <div>
                            <a href={paper.url || paper.URL} target="_blank" rel="noopener noreferrer">
                              {paper.name || paper["Paper Title"]}
                            </a>
                            <p>{paper.description}</p>
                          </div>

                          {paper.summary && (
                            <button
                              className="summary-button"
                              style={{ marginTop: "auto", width: "160px", alignSelf: "center" }}
                              onClick={() => handleViewSummary(paper.summary)}
                            >
                              View Summary
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {resources.length > 3 && (
                      <div style={{ marginTop: "10px" }}>
                        <button
                          className="nav-button"
                          onClick={() => toggleStudies(condition, name)}
                        >
                          {showAllStudies ? "Show less" : "Show more studies"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {selectedSummary && (
        <div className="summary-modal">
          <div className="modal-content">
            <button className="close-button" onClick={handleCloseSummary}>×</button>
            <h3 style={{ textAlign: "left" }}>Summary</h3>
            <div className="summary-scroll">
              <p className="sidebar-description-left">{selectedSummary}</p>
            </div>
          </div>
        </div>
      )}

      {showFeedback && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-button" onClick={() => setShowFeedback(false)}>×</button>
            <FeedbackForm />
          </div>
        </div>
      )}
    </>
  );
};

export default PersonalizedPage;
